import { useState } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { createTeamMember, updateTeamMember } from "../../../api/cms";

const TEAM_OPTIONS = [
  { value: "boardMembers", label: "Board members" },
  { value: "exco", label: "ExCo" },
  { value: "advisoryBoardMembers", label: "Advisory board members" },
  { value: "foundingMembers", label: "Founding members" },
];

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-sm font-medium">{label}{children}</label>
);

const inputClass = "border border-gray-300 rounded px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-bahyellow";

export default function AdminTeamMemberForm() {
  const existing = useLoaderData();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    image: "",
    linkedin: "",
    position: "",
    bio: [""],
    teams: [],
    ...existing,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const setBioLine = (i, value) =>
    setForm((f) => { const bio = [...f.bio]; bio[i] = value; return { ...f, bio }; });
  const addBioLine = () => setForm((f) => ({ ...f, bio: [...f.bio, ""] }));
  const removeBioLine = (i) =>
    setForm((f) => ({ ...f, bio: f.bio.filter((_, idx) => idx !== i) }));

  const toggleTeam = (value) =>
    setForm((f) => ({
      ...f,
      teams: f.teams.includes(value) ? f.teams.filter((t) => t !== value) : [...f.teams, value],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, bio: form.bio.filter(Boolean) };
      if (existing?.id) {
        await updateTeamMember(existing.id, payload);
      } else {
        await createTeamMember(payload);
      }
      navigate("/admin/team-members");
    } catch {
      setError("Failed to save. Check your API key and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">{existing?.id ? "Edit team member" : "Add team member"}</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 flex flex-col gap-5">
        <Field label="Full name">
          <input className={inputClass} value={form.name} onChange={set("name")} required />
        </Field>
        <Field label="Photo URL (S3 path or full URL)">
          <input className={inputClass} value={form.image} onChange={set("image")} />
          {form.image && <img src={form.image} alt="Preview" className="w-16 h-16 rounded-full object-cover mt-1" />}
        </Field>
        <Field label="LinkedIn URL">
          <input className={inputClass} value={form.linkedin} onChange={set("linkedin")} type="url" />
        </Field>
        <Field label="Position / title">
          <input className={inputClass} value={form.position} onChange={set("position")} />
        </Field>

        <div className="flex flex-col gap-1 text-sm font-medium">
          Bio lines
          {form.bio.map((line, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={`${inputClass} flex-1`}
                value={line}
                onChange={(e) => setBioLine(i, e.target.value)}
                placeholder={`Bio line ${i + 1}`}
              />
              <button type="button" onClick={() => removeBioLine(i)} className="text-red-400 hover:text-red-600 px-2">✕</button>
            </div>
          ))}
          <button type="button" onClick={addBioLine} className="self-start text-blue-600 hover:underline text-xs mt-1">
            + Add bio line
          </button>
        </div>

        <div className="flex flex-col gap-2 text-sm font-medium">
          Teams
          <div className="flex flex-wrap gap-4">
            {TEAM_OPTIONS.map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 font-normal cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.teams.includes(value)}
                  onChange={() => toggleTeam(value)}
                  className="accent-bahyellow"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={saving} className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => navigate("/admin/team-members")} className="rounded px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
